// src/features/delegations/pages/DelegationDetailPage/index.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Draw as SignIcon,
  AddCircleOutline as NewRevisionIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tokenService } from '../../../auth/services/tokenService';
import { useDelegationPermissions } from '../../../auth/hooks/usePermissions';
import { useDelegations } from '../../hooks/useDelegations';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import { DELEGATION_STATUS_COLOR } from '../../constants/delegationStatuses';
import DelegationTabs from './tabs';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

import PageHeader from '../../../../components/common/PageHeader';

export default function DelegationDetailPage() {
  const { t } = useTranslation(['delegations', 'common']);
  const { delegationId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canArchive, canDelete, canSign, canRevise } = useDelegationPermissions();

  const {
    currentDelegation: delegation,
    loading,
    error,
    clearError,
    fetchDelegation,
    signRevision,
    archiveDelegation,
    deleteDelegation,
  } = useDelegations();

  useEffect(() => {
    fetchDelegation(delegationId);
  }, [delegationId, fetchDelegation]);

  // ── Permissions ────────────────────────────────────────────────────────────
  const userId = tokenService.getUserId();
  const revision = delegation?.current_revision;

  const isParticipant =
    delegation &&
    (delegation.delegator?.staff_member_id === userId || delegation.recipient?.staff_member_id === userId);

  const hasSignedAlready =
    revision?.signatures?.some((s) => s.staff_member?.staff_member_id === userId) ?? false;

  const canSignNow =
    canSign &&
    isParticipant &&
    !hasSignedAlready &&
    ['DRAFT', 'UNDER_REVIEW', "ACTIVE"].includes(delegation?.status);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleNewRevision = () => {
    navigate('../create', {
      state: {
        delegationId,
        revisionPrefill: {
          title: revision?.title ?? '',
          task_description: revision?.task_description ?? '',
          steps: revision?.steps ?? '',
          competency_requirements: revision?.competency_requirements ?? '',
          contraindications: revision?.contraindications ?? '',
          supervision: revision?.supervision ?? '',
        },
      },
    });
  };

  // Opens the create form filled from this delegation; nothing is saved (or sent for
  // signing) until the user submits it. The recipient is left empty because a copy is
  // usually the same instruction for someone else.
  const handleCopy = () => {
    navigate('../create', {
      state: {
        copyPrefill: {
          delegator_id: delegation.delegator?.staff_member_id ?? '',
          title: revision?.title ?? '',
          task_description: revision?.task_description ?? '',
          steps: revision?.steps ?? '',
          competency_requirements: revision?.competency_requirements ?? '',
          contraindications: revision?.contraindications ?? '',
          supervision: revision?.supervision ?? '',
        },
      },
    });
  };

  const handleSign = () => {
    showConfirmDialog({
      title: t('delegations:detail.confirm.sign_title'),
      message: t('delegations:detail.confirm.sign_message'),
      onConfirm: async () => {
        const result = await signRevision(delegationId, revision.revision_id);
        if (result.success) {
          showSuccess(t('delegations:detail.success.signed'));
          fetchDelegation(delegationId);
        } else {
          showError(result.error);
        }
      },
    });
  };

  const handleArchive = () => {
    showConfirmDialog({
      title: t('delegations:detail.confirm.archive_title'),
      message: t('delegations:detail.confirm.archive_message'),
      severity: 'warning',
      onConfirm: async () => {
        const result = await archiveDelegation(delegationId);
        if (result.success) {
          showSuccess(t('delegations:detail.success.archived'));
          fetchDelegation(delegationId);
        } else {
          showError(result.error);
        }
      },
    });
  };

  const handleDelete = () => {
    showConfirmDialog({
      title: t('delegations:detail.confirm.delete_title'),
      message: t('delegations:detail.confirm.delete_message'),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteDelegation(delegationId);
        if (result.success) {
          showSuccess(t('delegations:detail.success.deleted'));
          navigateBack('..');
        } else {
          showError(result.error);
        }
      },
    });
  };

  // ── Loading / error guards ─────────────────────────────────────────────────
  if (loading && !delegation) {
    return (
      <Box maxWidth={860} mx="auto">
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error && !delegation) {
    return (
      <Box maxWidth={860} mx="auto">
        <Alert severity="error" onClose={clearError}>{error}</Alert>
      </Box>
    );
  }

  if (!delegation) return null;

  return (
    <Box flex="1" display="flex" flexDirection="column" minHeight={0}>
      <PageHeader
        onBack={() => navigateBack('..')}
        title={revision?.title ?? t('delegations:detail.untitled')}
        badges={
          <Chip
            label={t(`delegations:statuses.${delegation.status}`)}
            color={DELEGATION_STATUS_COLOR[delegation.status] || 'default'}
            size="small"
          />
        }
        subtitle={revision && t('delegations:detail.revision_number', { n: revision.revision_number })}
        actions={<>
          {canRevise && delegation.status !== 'ARCHIVED' && (
            <Button variant="outlined" startIcon={<NewRevisionIcon />} onClick={handleNewRevision}>
              {t('delegations:detail.buttons.new_revision')}
            </Button>
          )}
          {canSignNow && (
            <Button variant="contained" startIcon={<SignIcon />} onClick={handleSign}>
              {t('delegations:detail.buttons.sign')}
            </Button>
          )}
          {canArchive && delegation.status === 'ACTIVE' && (
            <Tooltip title={t('delegations:detail.buttons.archive')}>
              <IconButton onClick={handleArchive} color="warning">
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
          {canCreate && (
            <Tooltip title={t('common:buttons.copy')}>
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && delegation.status === 'DRAFT' && (
            <Tooltip title={t('delegations:detail.buttons.delete')}>
              <IconButton onClick={handleDelete} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </>}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>
      )}

      <DelegationTabs delegation={delegation} />

      <ConfirmDialog />
    </Box>
  );
}
